import * as React from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  executeViewQuery,
  ViewNode,
  useAppConfiguration,
  Category,
} from "../providers/AppConfiguration";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Document, db, recreateDatabase } from "../db";
import { doSignIn, doSignOut } from "../providers/jwt";
import {
  DocSynchroniserState,
  requestDocListSync,
  useDocumentSyncStatus,
} from "../providers/DocListSynchroniser";
import InSyncIcon from "@mui/icons-material/DownloadDone";
import UnauthorisedIcon from "@mui/icons-material/Login";
import NetworkErrorIcon from "@mui/icons-material/SyncDisabled";
import SynchronisingIcon from "@mui/icons-material/CloudSync";
import ViewList from "../components/ViewList";
import DocumentSubsection from "../components/DocumentSubsection";
import { register as registerServiceWorker } from "../serviceWorkerRegistration";

import {
  clearDownloadQueue,
  addToDownloadQueue,
  launchDownloadedFile,
  openOnDownload,
  startDownloader,
  stopDownloader,
} from "../providers/FileDownloader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ConfirmDownloadDialog from "../components/ConfirmDownloadDialog";
import TextField from "@mui/material/TextField";
import { tokenizer, stemmer, stopWordFilter, trimmer } from "lunr";
import { useLiveQuery } from "dexie-react-hooks";
import Alert from "@mui/material/Alert";
import LoginDialog from "../components/LoginDialog";
import Snackbar from "@mui/material/Snackbar";
import { useSearchIndex } from "../providers/SearchIndex";
import { Button } from "@mui/material";
import { closeSnackbar, useSnackbar } from "notistack";

import headerLogo from "../mrlLogo.png";
import {
  initializeClarity,
  setTag,
  trackEvent,
  ClarityEvents,
  ClarityTags,
} from "../services/clarity";

const drawerWidth = 300;

const syncStatusIcons: Record<DocSynchroniserState, React.ReactNode> = {
  inSync: <InSyncIcon />,
  networkError: <NetworkErrorIcon />,
  synchronising: <SynchronisingIcon />,
  unauthorized: <UnauthorisedIcon />,
};

const statusMessages: Record<DocSynchroniserState, String> = {
  inSync: "Synced",
  networkError: "Offline",
  synchronising: "Syncing",
  unauthorized: "Signed Out",
};

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  // padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

function sortByScore(a: DocumentAndOptionalScore, b: DocumentAndOptionalScore) {
  return (b.score ?? 0) - (a.score ?? 0);
}

function findViewByName(categories: Category[], name: string) {
  for (let cat of categories) {
    const v = cat.views.find((v) => v.name === name);

    if (v) {
      return v;
    }
  }
  return undefined;
}

function parseBool(val: string | null): boolean {
  return val === "true" || val === null;
}

export type DocumentAndOptionalScore = Document & { score?: number };

const emptyScoreMap = new Map<number, number>();

export default function App() {
  const appConfig = useAppConfiguration();
  const [sidebarOpen, setSidebarOpen] = React.useState(
    parseBool(sessionStorage.getItem("dcms:sidebarOpen")),
  );
  const [currentView, setCurrentView] = React.useState<ViewNode>({
    name: "All Documents (Search)",
    query: [],
    icon: "search",
  });

  const [searchText, setSearchText] = React.useState(
    sessionStorage.getItem("dcms:searchText") ?? "",
  );
  const { enqueueSnackbar } = useSnackbar();

  const { status: syncStatus, diskSpaceLimited } = useDocumentSyncStatus();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [confirmingSync, setConfirmingSync] = React.useState<Document[]>([]);
  const [loginDialogShown, setLoginDialogShown] = React.useState(false);
  const [displayOfflineSnack, setDisplayOfflineSnack] = React.useState(false);
  const [SWUpdateAvailable, setSWUpdateAvailable] = React.useState(false);
  const [expanded, setExpanded] = React.useState<string[]>(
    JSON.parse(sessionStorage.getItem("dcms:expanded") ?? "[]") as string[],
  );
  const [firstRender, setFirstRender] = React.useState(true);

  const mainSearchIndex = useSearchIndex();

  const docsInView = useLiveQuery(
    () =>
      currentView ? executeViewQuery(db, currentView) : db.documents.toArray(),
    [currentView],
    [],
  );

  const filteredDocs = React.useMemo(() => {
    const searchTokens = tokenizer(searchText)
      .map(trimmer)
      .map(stopWordFilter)
      .filter((t) => t !== undefined)
      .map(stemmer)
      .map((t) => t.toString());

    let res: DocumentAndOptionalScore[] = [...docsInView];
    res.forEach((d) => {
      d.score = 0;
    });

    const tokenToScores = searchTokens.map(
      (token) => mainSearchIndex.get(token) ?? emptyScoreMap,
    );
    // If we don't have any matches for a token, then return an emtpy set;
    if (tokenToScores.findIndex((ts) => ts === undefined) !== -1) {
      return [];
    }
    if (searchTokens.length > 0) {
      // Now merge the existing maps. into this one.  Documents must be present in all maps to feature in the result.
      for (let tokenScoreMap of tokenToScores) {
        for (let i = 0; i < res.length; i++) {
          const doc = res[i];
          const newScore = tokenScoreMap.get(doc.id);
          if (newScore !== undefined) {
            doc.score = (doc.score ?? 0) + newScore;
          } else {
            // Remove the item, then step back one so that our loop will still be on the right index
            res.splice(i--, 1);
          }
        }
      }
    }
    res.sort(sortByScore);
    return res;
  }, [searchText, docsInView, mainSearchIndex]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (firstRender && filteredDocs.length > 0) {
        const value = sessionStorage.getItem("dcms:scrollPosition");
        console.log("First render, restoring scroll to ", value);
        if (value) {
          window.scrollTo(0, parseInt(value, 10));
        }
        setFirstRender(false);
      }
    }, 100);
    return () => {
      clearTimeout(timeout);
    };
  }, [filteredDocs, firstRender]);

  React.useEffect(() => {
    if (diskSpaceLimited) {
      enqueueSnackbar({
        message:
          "This application has run out of space to store documents.  Please remove some files",
        variant: "error",
        key: "storage",
        persist: true,
        action: (
          <div>
            <Button
              variant="text"
              color="primary"
              onClick={() => closeSnackbar("storage")}
            >
              Remove old files
            </Button>
            <Button
              variant="text"
              color="secondary"
              onClick={() => closeSnackbar("storage")}
            >
              Dismiss
            </Button>
          </div>
        ),

        onClose: () => {},
      });
    }
  }, [diskSpaceLimited, enqueueSnackbar]);

  React.useEffect(() => {
    let storedViewName = sessionStorage.getItem("dcms:currentView");
    if (storedViewName) {
      // Deal with legacy values.
      const view = findViewByName(appConfig.categories, storedViewName);
      if (view) {
        setCurrentView(view);
      }
    }
  }, [appConfig]);

  // Initialize Clarity on app startup
  React.useEffect(() => {
    initializeClarity();

    // Set initial tags
    setTag(ClarityTags.ONLINE_STATUS, navigator.onLine ? "online" : "offline");
    setTag(
      ClarityTags.USER_TYPE,
      syncStatus === "unauthorized" ? "anonymous" : "authenticated",
    );
    setTag(ClarityTags.CURRENT_VIEW, currentView.name);
    setTag(ClarityTags.DOCUMENT_COUNT, filteredDocs.length.toString());
  }, [syncStatus, currentView.name, filteredDocs.length]);

  // Track sync status changes
  React.useEffect(() => {
    setTag(
      ClarityTags.USER_TYPE,
      syncStatus === "unauthorized" ? "anonymous" : "authenticated",
    );

    if (syncStatus === "inSync") {
      trackEvent(ClarityEvents.SYNC_COMPLETED);
    } else if (syncStatus === "networkError") {
      trackEvent(ClarityEvents.SYNC_ERROR);
      trackEvent(ClarityEvents.OFFLINE_MODE);
    }
  }, [syncStatus]);

  // Track view changes
  React.useEffect(() => {
    setTag(ClarityTags.CURRENT_VIEW, currentView.name);
    trackEvent(ClarityEvents.VIEW_CHANGED);
  }, [currentView.name]);

  // Track document count changes
  React.useEffect(() => {
    setTag(ClarityTags.DOCUMENT_COUNT, filteredDocs.length.toString());
  }, [filteredDocs.length]);

  // Track search activity
  React.useEffect(() => {
    if (searchText.trim()) {
      setTag(ClarityTags.SEARCH_QUERY, searchText.trim());
      trackEvent(ClarityEvents.SEARCH_PERFORMED);
    } else {
      trackEvent(ClarityEvents.SEARCH_CLEARED);
    }
  }, [searchText]);

  // Track online/offline status
  React.useEffect(() => {
    const handleOnline = () => {
      setTag(ClarityTags.ONLINE_STATUS, "online");
    };

    const handleOffline = () => {
      setTag(ClarityTags.ONLINE_STATUS, "offline");
      trackEvent(ClarityEvents.OFFLINE_MODE);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Run this effect at mount time (start time) - Do an initial doc sync, and start the downloader.
  React.useEffect(() => {
    requestDocListSync();
    startDownloader();

    const visChangeListener = () => {
      console.log("Visibility Change", new Date(), "hidden:", document.hidden);
      if (!document.hidden) {
        // Every time we come back, request a sync immediately.
        requestDocListSync();
      }
    };
    document.addEventListener("visibilitychange", visChangeListener);

    return () => {
      stopDownloader();
      document.removeEventListener("visibilitychange", visChangeListener);
    };
  }, []);

  React.useEffect(() => {
    registerServiceWorker({
      onUpdate: (reg) => {
        setSWUpdateAvailable(true);
      },
      onSuccess: (reg) => {
        console.log("WORKING");
      },
      statusUpdate: () => {},
    });
  }, []);

  React.useEffect(() => {
    setLoginDialogShown(syncStatus === "unauthorized");
  }, [syncStatus]);

  React.useEffect(() => {
    // Persist expanded values, so that they are restored if we reload.
    sessionStorage.setItem("dcms:expanded", JSON.stringify(expanded));
  }, [expanded]);

  //
  React.useEffect(() => {
    // Function to save scroll position
    const handleScroll = () => {
      sessionStorage.setItem("dcms:scrollPosition", String(window.scrollY));
    };
    // Set up scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up scroll event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // Empty dependency array ensures this effect runs only once on mount

  const handleSWUpdate = async () => {
    // Track app update event
    trackEvent(ClarityEvents.APP_UPDATED);

    const sw = await navigator.serviceWorker.ready;
    if (sw.waiting) {
      sw.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  };

  const updateOpen = (open: boolean) => {
    setSidebarOpen(open);
    sessionStorage.setItem("dcms:sidebarOpen", String(open));
  };
  const handleDrawerOpen = () => updateOpen(true);
  const handleDrawerClose = () => updateOpen(false);

  let statusIcon = syncStatusIcons[syncStatus] ?? syncStatusIcons.unauthorized;
  let statusText = statusMessages[syncStatus] ?? "Unknown";

  async function onFileOpened(doc: Document, target: string) {
    // Track document opening event
    trackEvent(ClarityEvents.DOCUMENT_OPENED);

    if (doc.stored_etag) {
      launchDownloadedFile(doc, target);
    } else {
      // TODO tailor message to suit online/offline.
      enqueueSnackbar(
        navigator.onLine
          ? "File will launch after download"
          : "File will download once on network",
        {
          variant: "info",
          autoHideDuration: 1000,
        },
      );
      addToDownloadQueue([doc]);
      if (navigator.onLine) {
        openOnDownload(doc.key, target);
      }
    }
  }
  const noDocsTxt =
    currentView?.name === "Download Queue"
      ? "No active downloads"
      : "No documents match this search criteria";

  function menuButtonPressed(event: React.MouseEvent<HTMLElement>) {
    setMenuAnchorEl(event.currentTarget);
  }

  function menuClose() {
    setMenuAnchorEl(null);
  }

  function sync() {
    menuClose();
    requestDocListSync();
  }

  async function clearDB() {
    menuClose();
    await recreateDatabase();
    requestDocListSync();
  }

  async function showHelp() {
    // Track help access
    trackEvent(ClarityEvents.HELP_ACCESSED);

    if (appConfig.helpDoc) {
      const helpDoc = await db.documents
        .where("key")
        .equals(appConfig.helpDoc)
        .first();
      console.log("Showing help doc", appConfig.helpDoc, helpDoc);
      if (helpDoc) {
        onFileOpened(helpDoc, "_self");
      } else {
        enqueueSnackbar({
          message: `help file ${appConfig.helpDoc} is not available`,
          variant: "error",
          autoHideDuration: 5000,
          key: "help_unavailable",
          onClose: () => {},
        });
      }
    }
  }

  function signIn() {
    menuClose();
    trackEvent(ClarityEvents.USER_SIGNED_IN);
    doSignIn();
  }

  function signOut() {
    trackEvent(ClarityEvents.USER_SIGNED_OUT);
    doSignOut();
  }

  function onFileSyncRequested(docs: Document[]) {
    // Track document sync requests
    trackEvent(ClarityEvents.DOCUMENT_SYNC_REQUESTED);

    if (docs.length < 10) {
      addToDownloadQueue(docs);
    } else {
      setConfirmingSync(docs);
    }
  }

  function downloadAll() {
    menuClose();
    trackEvent(ClarityEvents.DOCUMENT_DOWNLOADED);
    onFileSyncRequested(filteredDocs);
  }

  const handleLoginSubmit = async () => {
    // Check if user is online
    if (!navigator.onLine) {
      setDisplayOfflineSnack(true); // Show offline snackbar or message
      setLoginDialogShown(false);
      return; // Exit the function to prevent further execution
    }

    // If online, proceed with sign-in
    trackEvent(ClarityEvents.USER_SIGNED_IN);
    doSignIn();
  };

  const showDownloadAll = React.useMemo(
    () => currentView?.name.indexOf("Download") === -1,
    [currentView],
  );
  const showClearDownloadQueue = React.useMemo(
    () => currentView?.name === "Download Queue",
    [currentView],
  );

  const updateSearchText = React.useCallback(
    (newSearchText: string) => {
      setSearchText(newSearchText);
      sessionStorage.setItem("dcms:searchText", newSearchText);
    },
    [setSearchText],
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={sidebarOpen}
        style={{ background: "#000" }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(sidebarOpen && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flex: 1 }}>
            {currentView.name} ({filteredDocs.length})
          </Typography>
          {SWUpdateAvailable && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSWUpdate}
              sx={{ mx: 2 }} // Adds horizontal margin
            >
              Update App
            </Button>
          )}
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            color="#777"
          >
            {statusText}: {statusIcon}
          </Box>

          <div>
            <IconButton color="primary" onClick={menuButtonPressed} edge="end">
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu"
              aria-labelledby="Menu"
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={menuClose}
            >
              {syncStatus !== "unauthorized" ? (
                <MenuItem onClick={signOut}>Sign Out</MenuItem>
              ) : (
                <MenuItem onClick={signIn}>Sign In</MenuItem>
              )}

              <MenuItem onClick={sync}>Sync Documents</MenuItem>
              {showClearDownloadQueue && (
                <MenuItem
                  onClick={() => {
                    menuClose();
                    clearDownloadQueue();
                  }}
                >
                  Clear download queue
                </MenuItem>
              )}
              {showDownloadAll && (
                <MenuItem onClick={downloadAll}>
                  Download all files in view
                </MenuItem>
              )}
              <MenuItem onClick={clearDB}>Delete all cached data</MenuItem>

              {appConfig.helpDoc && (
                <MenuItem onClick={showHelp}>Help</MenuItem>
              )}
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Snackbar
        open={displayOfflineSnack}
        autoHideDuration={6000}
        message="You are currently offline. Synchronisation is disabled."
        ContentProps={{
          style: { backgroundColor: "red" },
        }}
        onClick={() => setDisplayOfflineSnack(false)} // Close on click
      />
      <Drawer
        open={sidebarOpen}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
      >
        <DrawerHeader>
          <img src={headerLogo} style={{ height: "36px" }} alt="MRL Logo" />
          <div style={{ padding: "8px", flex: 1 }}>
            <Typography variant="h5">
              {appConfig.appTitle ?? "Documents"}
            </Typography>
            <Typography
              variant="subtitle1"
              fontSize={8}
              sx={{ paddingLeft: 1, addingBottom: 1 }}
            >
              {process.env.REACT_APP_VERSION ?? "Development"}
            </Typography>
          </div>

          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <ViewList
          selectedView={currentView}
          onViewSelected={(v) => {
            if (v !== currentView) {
              setCurrentView(v);
              sessionStorage.setItem("dcms:currentView", v.name);
              sessionStorage.removeItem("dcms:expanded");
              sessionStorage.removeItem("dcms:scrollPosition");
              // If we're on a small device, auto-close the task bar when you select a view.
              if (window.innerWidth <= 800) {
                updateOpen(false);
              }
              updateSearchText("");
            }
          }}
        />
      </Drawer>
      <LoginDialog
        open={loginDialogShown}
        handleSubmit={handleLoginSubmit}
        onClose={() => setLoginDialogShown(false)}
      />

      <Main open={sidebarOpen}>
        <Toolbar />
        <div>
          <TextField
            type="search"
            placeholder="Filter"
            value={searchText}
            onChange={(evt) => updateSearchText(evt.target.value)}
            aria-label="search"
            sx={{ width: "100%", padding: 0.5 }}
            variant="standard"
            InputProps={{
              startAdornment: <SearchIcon sx={{ marginRight: 1 }} />,
            }}
          />
        </div>

        {confirmingSync && (
          <ConfirmDownloadDialog
            docs={confirmingSync}
            onClose={() => setConfirmingSync([])}
          />
        )}

        {filteredDocs.length === 0 ? (
          <Box sx={{ p: 1 }}>
            <Alert color="info">{noDocsTxt}</Alert>
          </Box>
        ) : (
          <DocumentSubsection
            onFileSyncRequested={onFileSyncRequested}
            onFileOpened={onFileOpened}
            docs={filteredDocs}
            breakdown={searchText.trim() ? [] : (currentView?.children ?? [])}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        )}
      </Main>
    </Box>
  );
}
